import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import PlayCard from "../components/PlayCard";
import DailyPlanCard, { PlaysGrid } from "../components/dashboard/DailyPlanCard";
import ResearchAnalyst from "../components/dashboard/ResearchAnalyst";
import { RefreshCw, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const SPORTS = [
  { key: "mlb", label: "MLB" },
  { key: "nba", label: "NBA" },
  { key: "nhl", label: "NHL" },
  { key: "nfl", label: "NFL" },
  { key: "mma", label: "UFC/MMA" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [active, setActive] = useState("mlb");
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [daily, setDaily] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);

  const loadPlays = useCallback(async (sport) => {
    setLoading(true);
    setPlays([]);
    try {
      const { data } = await api.get(`/sports/${sport}/plays`, { params: { limit: 5 } });
      setPlays(data.plays || []);
    } catch (err) {
      console.warn(`Could not load ${sport} plays`, err);
      setPlays([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDaily = useCallback(async () => {
    setDailyLoading(true);
    try {
      const { data } = await api.get("/daily-plan");
      setDaily(data);
    } catch (err) {
      console.warn("Could not load daily plan", err);
    } finally {
      setDailyLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlays(active);
  }, [active, loadPlays]);

  useEffect(() => {
    loadDaily();
  }, [loadDaily]);

  return (
    <div data-testid="dashboard">
      {/* Research Analyst — deep 1000-game evaluator at the top */}
      <ResearchAnalyst />

      {/* Header block */}
      <div className="grid lg:grid-cols-12 gap-8 border-b border-zinc-800 pb-10 mb-10">
        <div className="lg:col-span-7">
          <div className="overline mb-3">TODAY·BRIEFING</div>
          <h1 className="font-display text-4xl lg:text-5xl leading-tight">
            Welcome back, <span className="text-[#00D4FF]">{user?.name || "handicapper"}</span>.
          </h1>
          <p className="mt-4 text-zinc-400 max-w-xl">
            Here's today's slate across MLB, NBA, NHL, NFL and UFC — graded by the AI analyst
            and mapped to a Martingale progression sized to your bankroll.
          </p>
        </div>

        <div className="lg:col-span-5 border border-zinc-800 p-6 bg-zinc-950/50">
          <div className="flex items-center justify-between mb-4">
            <span className="overline neon-text">DAILY PLAN</span>
            <button
              onClick={loadDaily}
              disabled={dailyLoading}
              data-testid="daily-refresh"
              className="flex items-center gap-1 text-xs mono text-zinc-400 hover:text-[#00D4FF]"
            >
              {dailyLoading ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} strokeWidth={1.5} />
              )}
              REFRESH
            </button>
          </div>
          <DailyPlanCard daily={daily} loading={dailyLoading} />
        </div>
      </div>

      {/* Sport tabs */}
      <div className="flex flex-wrap gap-0 mb-0 border-b border-zinc-800">
        {SPORTS.map((s) => (
          <button
            key={s.key}
            data-testid={`tab-${s.key}`}
            data-active={active === s.key}
            onClick={() => setActive(s.key)}
            className="tab-terminal"
          >
            {s.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={() => loadPlays(active)}
          disabled={loading}
          data-testid="plays-refresh"
          className="tab-terminal !border-zinc-800 flex items-center gap-2"
        >
          {loading ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <RefreshCw size={12} strokeWidth={1.5} />
          )}
          Refresh
        </button>
      </div>

      {/* Plays grid */}
      <div className="pt-8">
        <PlaysGrid plays={plays} loading={loading} active={active} PlayCard={PlayCard} />
      </div>
    </div>
  );
}
